import { useEffect, useRef, useState } from "react";
import { FileSignature, FileText, Trash2, Upload, Download, BadgeCheck, FileCheck2 } from "lucide-react";
import { api, apiError, contratoBlobUrl, descargarContratoPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fecha, fechaLarga } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Documentos() {
  const { activeId, activeStudent, user } = useAuth();
  const [contrato, setContrato] = useState(null);
  const [docs, setDocs] = useState([]);
  const [firmar, setFirmar] = useState(false);
  const [nombre, setNombre] = useState(user?.nombre || "");
  const [tipo, setTipo] = useState("Certificado médico");
  const [verPdf, setVerPdf] = useState(null);
  const fileRef = useRef();

  const abrirContratoPdf = async () => {
    setVerPdf("cargando");
    try {
      setVerPdf(await contratoBlobUrl(activeId));
    } catch (e) {
      setVerPdf(null);
      toast.error(apiError(e, "No se pudo abrir el contrato"));
    }
  };

  const cargar = () => {
    api.get(`/estudiantes/${activeId}/contrato`).then((r) => setContrato(r.data));
    api.get(`/estudiantes/${activeId}/documentos`).then((r) => setDocs(r.data));
  };

  useEffect(() => {
    if (!activeId) return;
    setContrato(null);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const doFirmar = async () => {
    try {
      await api.post(`/estudiantes/${activeId}/contrato/firmar`, { firmado_por: nombre });
      toast.success("Contrato firmado electrónicamente");
      setFirmar(false);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const subir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/estudiantes/${activeId}/documentos`, {
          estudiante_id: activeId, tipo, nombre: file.name, archivo_base64: String(reader.result),
        });
        toast.success("Documento subido");
        cargar();
      } catch (err) {
        toast.error(apiError(err));
      }
    };
    reader.readAsDataURL(file);
  };

  const eliminar = async (id) => {
    await api.delete(`/documentos/${id}`);
    toast.success("Documento eliminado");
    cargar();
  };

  const descargarContrato = () => {
    const txt = `${contrato.contenido}\n\nEstudiante: ${activeStudent?.nombre} · Matrícula ${activeStudent?.matricula}\n` +
      (contrato.firmado ? `Firmado electrónicamente por: ${contrato.firmado_por} el ${fechaLarga(contrato.fecha_firma)}`
        : "PENDIENTE DE FIRMA");
    const url = URL.createObjectURL(new Blob([txt], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Contrato-${activeStudent?.matricula}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeId) return <Empty>No hay estudiantes asociados.</Empty>;
  if (!contrato) return <Loading />;

  return (
    <div data-testid="documents-page">
      <PageTitle title="Documentos y contratos"
                 subtitle="Contrato de ingreso del año lectivo y expediente documental del estudiante.">
        <Badge variant={contrato.firmado ? "secondary" : "destructive"} data-testid="contrato-badge">
          {contrato.firmado ? "Firmado" : "Pendiente de firma"}
        </Badge>
      </PageTitle>

      <div className="grid gap-8 md:grid-cols-12">
        <section className="md:col-span-7 rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-primary" />
            <h2 className="font-display text-xl font-bold tracking-tight">
              Contrato de ingreso {contrato.anio_lectivo}
            </h2>
          </div>
          <pre className="mt-6 max-h-[420px] overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/40 p-6 text-sm leading-relaxed"
               data-testid="contrato-contenido">{contrato.contenido}</pre>

          {contrato.pdf_nombre && (
            <div className="mt-6 rounded-md border border-primary/30 bg-primary/5 p-5" data-testid="contrato-pdf-info">
              <div className="flex items-start gap-3">
                <FileCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{contrato.pdf_nombre}</p>
                  <p className="text-xs text-muted-foreground">
                    Contrato oficial publicado por el colegio · {fecha(contrato.pdf_subido_en)}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" className="rounded-full" onClick={abrirContratoPdf}
                        data-testid="ver-contrato-pdf">
                  Ver contrato en PDF
                </Button>
                <Button size="sm" variant="outline" className="rounded-full"
                        onClick={() => descargarContratoPdf(activeId, contrato.pdf_nombre)}
                        data-testid="descargar-contrato-pdf">
                  <Download className="mr-2 h-4 w-4" /> Descargar PDF
                </Button>
              </div>
            </div>
          )}

          {contrato.firmado ? (
            <div className="mt-6 flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 p-5"
                 data-testid="contrato-firmado-info">
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="text-sm">
                <p className="font-semibold">Firmado por {contrato.firmado_por}</p>
                <p className="text-muted-foreground">{fechaLarga(contrato.fecha_firma)}</p>
              </div>
            </div>
          ) : (
            <Button className="mt-6 rounded-full" onClick={() => setFirmar(true)} data-testid="abrir-firma">
              Aceptar y firmar digitalmente
            </Button>
          )}
          <Button variant="outline" className="mt-3 ml-0 sm:ml-3 rounded-full" onClick={descargarContrato}
                  data-testid="descargar-contrato">
            <Download className="mr-2 h-4 w-4" /> Descargar
          </Button>
        </section>

        <aside className="md:col-span-5 rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold tracking-tight">Expediente del estudiante</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Suba certificados médicos, permisos o fotografías actualizadas.
          </p>

          <div className="mt-6 space-y-3">
            <Label>Tipo de documento</Label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="doc-tipo"
                    className="w-full rounded-md border border-border bg-background px-4 py-2 text-sm">
              {["Certificado médico", "Permiso de salida", "Fotografía", "Acta de nacimiento", "Otro"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input ref={fileRef} type="file" className="hidden" onChange={subir} data-testid="doc-file-input" />
            <Button variant="outline" className="w-full rounded-full" onClick={() => fileRef.current?.click()}
                    data-testid="subir-documento">
              <Upload className="mr-2 h-4 w-4" /> Subir archivo
            </Button>
          </div>

          <div className="mt-8 space-y-3" data-testid="lista-documentos">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-md border border-border/60 p-4">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{d.nombre}</p>
                  <p className="text-xs text-muted-foreground">{d.tipo} · {fecha(d.creado_en)}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => eliminar(d.id)}
                        data-testid={`eliminar-doc-${d.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            {docs.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay documentos cargados.</p>}
          </div>
        </aside>
      </div>

      <Dialog open={!!verPdf} onOpenChange={(o) => !o && setVerPdf(null)}>
        <DialogContent className="max-w-3xl" data-testid="modal-contrato-pdf">
          <DialogHeader>
            <DialogTitle className="font-display">Contrato de ingreso</DialogTitle>
            <DialogDescription>{contrato?.pdf_nombre} · {activeStudent?.nombre}</DialogDescription>
          </DialogHeader>
          {verPdf && verPdf !== "cargando" ? (
            <>
              <iframe src={verPdf} title="Contrato en PDF" data-testid="visor-contrato"
                      className="h-[65vh] w-full rounded-md border border-border" />
              <a href={verPdf} target="_blank" rel="noreferrer" data-testid="abrir-contrato-nueva-pestana"
                 className="text-center text-xs text-primary hover:underline">
                Abrir el contrato en una pestaña nueva
              </a>
            </>
          ) : (
            <div className="flex h-[65vh] items-center justify-center text-sm text-muted-foreground">
              Cargando contrato…
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={firmar} onOpenChange={setFirmar}>
        <DialogContent data-testid="modal-firma">
          <DialogHeader>
            <DialogTitle className="font-display">Firma electrónica del contrato</DialogTitle>
            <DialogDescription>
              Al escribir su nombre completo acepta los términos del contrato conforme a la Ley 126-02.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo del tutor</Label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="firma-nombre" />
            </div>
            <Button className="w-full rounded-full py-6" onClick={doFirmar} data-testid="confirmar-firma">
              Firmar contrato
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
